import React from "react";
import { Card, StatusPill } from "@/ds";
import type { HamburgCutoffChangeView, HamburgCutoffView, MessageView } from "@/api";
import { useLang, useT, type Lang, type TFunc } from "../../i18n";
import { valToStr } from "../fact-value";
import styles from "./email-review.module.css";

function copyKey(cutoff: HamburgCutoffView): string {
  if (cutoff.outcome === "ON_TIME") return "onTime";
  if (cutoff.outcome === "LATE_INITIAL_SUBMISSION") return "lateInitial";
  if (cutoff.outcome === "LATE_MATERIAL_CHANGE") return "lateChange";
  if (cutoff.outcome === "CHANGE_FORBIDDEN") return "placed";
  return cutoff.reason_code === "CALENDAR_OUT_OF_RANGE" ? "outOfRange" : "unproven";
}

function formattedInstant(value: string | null, lang: Lang): string {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime())
    ? parsed.toLocaleString(lang === "de" ? "de-DE" : "en-US", {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: "Europe/Berlin",
      })
    : "—";
}

function sourceLabel(
  sourceMessageId: string | null,
  messages: MessageView[],
  t: TFunc,
): string {
  if (!sourceMessageId) return t("ws.cutoff.correctionTime");
  const inbound = messages.filter((message) => message.direction === "inbound" && message.id);
  const index = inbound.findIndex(({ id }) => id === sourceMessageId);
  return index === -1
    ? t("ws.cutoff.sourceEmail")
    : t("ws.sourceEmailMessage", { n: index + 1 });
}

function ChangeList({
  title,
  changes,
  messages,
}: {
  title: string;
  changes: HamburgCutoffChangeView[];
  messages: MessageView[];
}) {
  const t = useT();
  if (changes.length === 0) return null;
  return (
    <section className={styles.cutoffChanges}>
      <h4>{title}</h4>
      <ul>
        {changes.map((change) => (
          <li key={`${change.field_path}:${change.source_message_id ?? "manual"}`}>
            <span>
              <strong>{t(`field.${change.field_path}`, undefined, t("ws.fieldWithoutLabel"))}</strong>
              <span>{valToStr(change.value, t)}</span>
            </span>
            <small>{sourceLabel(change.source_message_id, messages, t)}</small>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function HamburgCutoffStatus({
  cutoff,
  messages,
}: {
  cutoff: HamburgCutoffView | null | undefined;
  messages: MessageView[];
}) {
  const t = useT();
  const { lang } = useLang();
  if (!cutoff) return null;
  const key = copyKey(cutoff);
  const tone = cutoff.outcome === "ON_TIME"
    ? "success" as const
    : cutoff.outcome === "UNRESOLVED"
      ? "warning" as const
      : "danger" as const;
  return (
    <Card
      title={t("ws.cutoff.title")}
      actions={<StatusPill tone={tone} label={t(`ws.cutoff.${key}.title`)} />}
    >
      <div className={styles.cutoffStatus} data-testid="hamburg-cutoff-status">
        <p>{t(`ws.cutoff.${key}.body`)}</p>
        <dl>
          <div>
            <dt>{t("ws.cutoff.authoritativeAt")}</dt>
            <dd>{formattedInstant(cutoff.authoritative_at, lang)}</dd>
          </div>
          <div>
            <dt>{t("ws.cutoff.cutoffAt")}</dt>
            <dd>{formattedInstant(cutoff.cutoff_at, lang)}</dd>
          </div>
          <div>
            <dt>{t("ws.cutoff.calendar")}</dt>
            <dd>{cutoff.calendar_version} · {cutoff.time_zone}</dd>
          </div>
        </dl>
        <div className={styles.cutoffDispositionGrid}>
          <ChangeList
            title={t("ws.cutoff.applied")}
            changes={cutoff.applied_changes}
            messages={messages}
          />
          <ChangeList
            title={t("ws.cutoff.rejected")}
            changes={cutoff.rejected_changes}
            messages={messages}
          />
        </div>
      </div>
    </Card>
  );
}
